import WorkModelSettingsPanel from './WorkModelSettingsPanel';
import { useWorkExecution } from './WorkExecutionContext';
import WorkApprovalButton from './WorkApprovalButton';
import WorkModelTriggerContent from './WorkModelTriggerContent';
import ManualAiSharingConsentPrompt from '@/components/Auth/ManualAiSharingConsentPrompt';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  ScrollView,
  View,
  useWindowDimensions,
} from 'react-native';
import PropTypes from 'prop-types';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/context/ThemeContext';
import Tooltip from '@/components/Tooltip';
import { useChatStore, useCoworkerStore, useToastStore } from '@/store';
import { coworkerApi } from '@/lib/api';
import useTranslation from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import useComposerKeyboardHeight from '@/hooks/useComposerKeyboardHeight';
import { isWorkActive } from '@/constants/work';
import { thinkingPowerById } from '@/constants/workThinkingPower';

function WorkThinkingPowerTrigger({
  level,
  modelLabel,
  autoApprove = false,
  compact = false,
  isDisabled = false,
  hideTooltip = false,
  menuTrigger = false,
  onPress,
}) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const tooltip = Platform.OS === 'macos' || Platform.OS === 'web' ? 'Choose the main model, thinking level, and specialist intelligence' : t('chatInput.thinkingPower.tooltip');
  const label = (modelLabel ? `Main model: ${modelLabel}` : t('chatInput.thinkingPower.triggerA11y', { level: level.label })) + (autoApprove ? ', Auto-approve credits on' : '');
  // Desktop keeps the bolt; the compact (mobile / narrow) trigger shows the
  // current main-model selection instead of a brain icon.
  const triggerLabel = modelLabel || level.label;
  const Trigger = menuTrigger ? DropdownMenu.Trigger : Pressable;

  return (
    <Tooltip label={tooltip} disabled={isDisabled || hideTooltip}>
      <Trigger
        {...(!menuTrigger ? { onPress } : {})}
        disabled={isDisabled}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityHint={tooltip}
        {...(Platform.OS === 'macos' ? {
          enableFocusRing: false,
          onFocus: () => setFocused(true),
          onBlur: () => setFocused(false),
        } : {})}
      >
        <WorkModelTriggerContent label={triggerLabel} autoApprove={autoApprove} compact={compact}
          disabled={isDisabled} highlighted={hovered || focused} colors={colors} />
      </Trigger>
    </Tooltip>
  );
}

WorkThinkingPowerTrigger.propTypes = {
  level: PropTypes.object.isRequired,
  modelLabel: PropTypes.string,
  autoApprove: PropTypes.bool,
  compact: PropTypes.bool,
  isDisabled: PropTypes.bool,
  hideTooltip: PropTypes.bool,
  menuTrigger: PropTypes.bool,
  onPress: PropTypes.func,
};

function WorkThinkingPowerButton({
  conversationId = null,
  compact = false,
  isDisabled = false,
}) {
  const execution = useWorkExecution();
  isDisabled = isDisabled || execution?.disabled;
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { width: viewportWidth, height: viewportHeight } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const safe = Platform.OS === 'ios' ? insets : { left: 0, right: 0, top: 0, bottom: 0 };
  const keyboardHeight = useComposerKeyboardHeight();
  const bottomInset = Platform.OS === 'ios' ? Math.max(safe.bottom, keyboardHeight) : 0;
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [menuHeight, setMenuHeight] = useState(300);
  const triggerRef = useRef(null);
  const { addToast } = useToastStore((state) => state.actions);
  const composerThinkingPower = useCoworkerStore((s) => s.composerThinkingPower);
  const setComposerThinkingPower = useCoworkerStore((s) => s.setComposerThinkingPower);
  const setTaskModel = useCoworkerStore((s) => s.setTaskModel);
  const composerAutoApprove = useCoworkerStore((s) => s.composerAutoApprove);
  const setComposerAutoApprove = useCoworkerStore((s) => s.setComposerAutoApprove);
  const setTaskAutoApprove = useCoworkerStore((s) => s.setTaskAutoApprove);
  const [savingAutoApprove, setSavingAutoApprove] = useState(false);
  const taskId = useChatStore((state) => {
    if (!conversationId || conversationId === 'new') return null;
    const chat = state.chats.find((c) => c.id === conversationId);
    const raw = chat?.coworkerSession;
    if (!raw) return null;
    if (typeof raw === 'object') return String(raw._id || raw.id || '') || null;
    return String(raw);
  });
  const taskStatus = useCoworkerStore((s) => (taskId ? s.tasks[taskId]?.status : null));
  const openAbove = Platform.OS === 'macos' || Boolean(conversationId && conversationId !== 'new');
  const taskThinkingPower = useCoworkerStore((s) => (taskId ? s.tasks[taskId]?.thinkingPower : null));
  const taskAutoApprove = useCoworkerStore((s) => (taskId ? s.tasks[taskId]?.autoApprove : null));
  const storedModelSlug = useCoworkerStore((s) => (
    taskId ? s.tasks[taskId]?.orchestratorModel : s.composerOrchestrator?.orchestratorModel
  )) || '';
  const modelSlug = execution ? execution.settings.orchestratorModel : storedModelSlug;
  const [models, setModels] = useState(null);
  // Every platform shows the main-model control, so the trigger can label
  // itself "Auto" / the selected model and the menu can switch it.
  useEffect(() => {
    if (execution || !modelSlug || models) return;
    let current = true;
    coworkerApi.getOrchestratorModels()
      .then(items => { if (current) setModels(items); })
      .catch(() => {}); // Keep the selected slug visible if the catalog is unavailable.
    return () => { current = false; };
  }, [modelSlug, models, !!execution]);
  // An automatically assigned task.model is still an Auto selection.
  const modelLabel = modelSlug
    ? (execution?.models || models)?.find(item => item.slug === modelSlug)?.name || modelSlug
    : 'Auto';
  const autoApprove = execution ? execution.settings.autoApprove : taskId ? taskAutoApprove === true : composerAutoApprove;
  const changeAutoApprove = useCallback(async (enabled) => {
    if (!execution && !taskId) { setComposerAutoApprove(enabled); return; }
    setSavingAutoApprove(true);
    try { if (execution) await execution.update({ autoApprove: enabled }); else await setTaskAutoApprove(taskId, enabled); }
    catch (err) { addToast(err?.message || 'Failed to change Auto-approve', 'error'); }
    finally { setSavingAutoApprove(false); }
  }, [taskId, setComposerAutoApprove, setTaskAutoApprove, addToast, execution]);

  const level = useMemo(() => (
    thinkingPowerById(execution ? execution.settings.thinkingPower : taskThinkingPower || composerThinkingPower)
  ), [composerThinkingPower, taskThinkingPower, execution?.settings.thinkingPower]);

  const commitLevel = useCallback(async (next) => {
    if (!next || next.id === level.id) return;
    if (execution) {
      try { await execution.update({ thinkingPower: next.id }); }
      catch (err) { addToast(err?.message || t('chatInput.thinkingPower.switchFailed'), 'error'); }
      return;
    }
    setComposerThinkingPower(next.id);
    if (!taskId) return;
    try {
      const out = await setTaskModel(taskId, next.id);
      if (out?.applied === 'next_turn' && isWorkActive(taskStatus)) {
        addToast(t('chatInput.thinkingPower.switchedNextTurn', { level: next.label }), 'default');
      }
    } catch (err) {
      addToast(err?.message || t('chatInput.thinkingPower.switchFailed'), 'error');
    }
  }, [addToast, level.id, setComposerThinkingPower, setTaskModel, t, taskId, taskStatus, execution]);

  const openPopover = useCallback((event) => {
    const fromEvent = event?.currentTarget;
    const fromRef = triggerRef.current;
    const place = (rect) => {
      setAnchor(rect || null);
      setOpen(true);
    };
    const node = (fromEvent && typeof fromEvent.getBoundingClientRect === 'function')
      ? fromEvent
      : fromRef;
    if (node && typeof node.getBoundingClientRect === 'function') {
      place(node.getBoundingClientRect());
      return;
    }
    if (fromRef && typeof fromRef.measureInWindow === 'function') {
      fromRef.measureInWindow((x, y, width, height) => {
        place({
          top: y,
          left: x,
          width,
          height,
          bottom: y + height,
          right: x + width,
        });
      });
      return;
    }
    place(null);
  }, []);

  const showCompact = compact || isXS;
  useEffect(() => {
    if (!open || Platform.OS === 'web' || Platform.OS === 'macos') return undefined;
    const frame = requestAnimationFrame(() => openPopover());
    return () => cancelAnimationFrame(frame);
  }, [open, viewportWidth, viewportHeight, openPopover]);
  const sheet = isXS || !anchor;
  const fitsBelow = anchor && viewportHeight - anchor.bottom >= menuHeight + 8;
  const fitsAbove = anchor && anchor.top >= menuHeight + 8;
  const placeAbove = fitsAbove && (openAbove || !fitsBelow);
  const menuTop = anchor ? Math.max(safe.top + 8, Math.min(
    placeAbove ? anchor.top - menuHeight - 6 : anchor.bottom + 6,
    viewportHeight - bottomInset - menuHeight - 8,
  )) : safe.top + 8;
  const cardStyle = sheet
    ? styles.modalCard
    : [
      styles.popoverCard,
      { top: menuTop },
      { right: Platform.OS === 'ios'
        ? Math.max(safe.right + 8, Math.min(viewportWidth - anchor.right, viewportWidth - safe.left - 308))
        : Math.max(8, viewportWidth - anchor.right) },
    ];

  const panel = <WorkModelSettingsPanel taskId={taskId} models={models} onModelsLoaded={setModels} execution={execution}
    level={level} onLevelChange={commitLevel} autoApprove={autoApprove} onAutoApproveChange={changeAutoApprove}
    autoApproveDisabled={savingAutoApprove || taskStatus === 'queued'} colors={colors} open={open}
    consentHost={Platform.OS === 'ios' ? 'work-model' : 'app'} />;

  // This target does not register RCTModalHostView. Use the Mac overlay host,
  // which anchors to the trigger and handles outside clicks and Escape.
  if (Platform.OS === 'macos') return (
    <View style={styles.controls}>
      <WorkApprovalButton taskId={taskId} compact={showCompact} isDisabled={isDisabled} openAbove={openAbove} />
      <DropdownMenu.Root open={open} onOpenChange={setOpen}>
        <WorkThinkingPowerTrigger level={level} modelLabel={modelLabel} autoApprove={autoApprove} compact={showCompact}
          isDisabled={isDisabled} hideTooltip={open} menuTrigger />
        <DropdownMenu.Content side={openAbove ? 'top' : 'bottom'} align="end" sideOffset={8}
          // Keep the same viewport while the model list, reasoning controls,
          // help text and save notices change. Overflow scrolls inside it.
          style={{ width: Math.min(360, viewportWidth - 24), maxWidth: viewportWidth - 24,
            height: Math.min(540, viewportHeight - 24), maxHeight: viewportHeight - 24 }}>
          <View style={{ paddingHorizontal: 14, paddingVertical: 8 }}>{panel}</View>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </View>
  );

  return (
    <View style={styles.controls} ref={triggerRef} {...(Platform.OS === 'web' ? {} : { collapsable: false })}>
      <WorkApprovalButton taskId={taskId} compact={showCompact} isDisabled={isDisabled} openAbove={openAbove} />
      <WorkThinkingPowerTrigger
        level={level}
        modelLabel={modelLabel}
        autoApprove={autoApprove}
        compact={showCompact}
        isDisabled={isDisabled}
        hideTooltip={open}
        onPress={openPopover}
      />
      <Modal
        visible={open}
        transparent
        animationType={sheet ? 'fade' : 'none'}
        onRequestClose={() => setOpen(false)}
      >
        <Pressable
          style={[sheet ? styles.modalBackdrop : styles.popoverBackdrop, sheet && {
            paddingLeft: safe.left + 16, paddingRight: safe.right + 16,
            paddingTop: safe.top + 16, paddingBottom: Math.max(16, bottomInset),
          }]}
          onPress={() => setOpen(false)}
        >
          <Pressable
            testID="work-model-card"
            onPress={() => {}}
            onLayout={(event) => setMenuHeight(event.nativeEvent.layout.height)}
            style={[
              cardStyle,
              Platform.OS === 'ios' && { maxWidth: viewportWidth - safe.left - safe.right - 32 },
              Platform.OS === 'web' && { width: Math.min(360, viewportWidth - 32), maxWidth: viewportWidth - 32 },
              {
                backgroundColor: colors.solid_color_primary,
                borderColor: colors.border_color_secondary,
              },
            ]}
          >
            <ScrollView
              style={{ maxHeight: Math.max(Platform.OS === 'ios' ? 80 : 180, viewportHeight - safe.top - bottomInset - 140) }}
              keyboardShouldPersistTaps="handled"
              // Android needs this when the panel itself contains a scroller
              // (the main-model controls render their own ScrollView).
              nestedScrollEnabled={Platform.OS === 'android'}
              {...(Platform.OS === 'web' ? {} : { bounces: false })}
            >
              {panel}
            </ScrollView>
          </Pressable>
        </Pressable>
        {open && Platform.OS === 'ios' && <ManualAiSharingConsentPrompt host="work-model" inline insets={safe} />}
      </Modal>
    </View>
  );
}

WorkThinkingPowerButton.propTypes = {
  conversationId: PropTypes.string,
  collectionId: PropTypes.string,
  compact: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

const styles = StyleSheet.create({
  controls: { flexDirection: 'row', alignItems: 'center', flexShrink: 1, minWidth: 0 },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
    padding: 16,
  },
  modalCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 14,
    marginBottom: 24,
  },
  popoverBackdrop: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  popoverCard: {
    position: 'absolute',
    width: 300,
    borderRadius: 16,
    borderWidth: 1,
    padding: 12,
    ...Platform.select({
      web: { boxShadow: '0 10px 38px -10px rgba(0,0,0,0.45), 0 10px 20px -15px rgba(0,0,0,0.25)' },
      default: {
        shadowColor: '#000',
        shadowOpacity: 0.35,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 8 },
        elevation: 8,
      },
    }),
  },
});

export default React.memo(WorkThinkingPowerButton);
